import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInput } from "@emerald-react/input";
import classNames from "classnames";
import type { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent } from "react";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import {
  AccordionGroupContextType,
  AccordionGroupProps,
  AccordionProps,
} from "./types";

// Context for managing accordion group state
const AccordionGroupContext = createContext<AccordionGroupContextType | null>(
  null,
);

const useAccordionGroup = (): AccordionGroupContextType => {
  const context = useContext(AccordionGroupContext);
  if (!context) {
    throw new Error("Accordion must be used within an AccordionGroup");
  }
  return context;
};

// AccordionGroup Component
export const AccordionGroup = ({
  children,
  allowMultiple = true,
  className = "",
}: AccordionGroupProps) => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (id: string) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        if (!allowMultiple) {
          newSet.clear();
        }
        newSet.add(id);
      }

      return newSet;
    });
  };

  const contextValue = {
    openItems,
    toggleItem,
    allowMultiple,
  };

  return (
    <AccordionGroupContext.Provider value={contextValue}>
      <div className={classNames("accordion-group", className)}>{children}</div>
    </AccordionGroupContext.Provider>
  );
};

// Add displayName for better debugging
AccordionGroup.displayName = "AccordionGroup";

// Accordion Component
export const Accordion = ({
  id,
  title,
  children,
  leftElement,
  rightElement,
  className = "",
  headerClassName = "",
  contentClassName = "",
  disabled = false,
  defaultOpen = false,
  titleEditable = false,
  titleEditValue,
  titleInputClass = "",
  titleNumber,
  onTitleChange,
  onTitleKeyDown,
  onTitleBlur,
  onToggle, // New prop
  titleError,
}: AccordionProps) => {
  const { openItems, toggleItem } = useAccordionGroup();
  const uniqueId = useId();
  const contentInnerRef = useRef<HTMLDivElement>(null);
  const hasSetInitialState = useRef(false);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [titleValue, setTitleValue] = useState<string>(
    titleEditValue || (typeof title === "string" ? title : ""),
  );

  // Generate unique ID if not provided - prefer provided ID, fallback to React 18 useId
  const accordionId = id || `accordion-${uniqueId}`;

  // Set default open state if specified (only once)
  useEffect(() => {
    if (
      defaultOpen &&
      !hasSetInitialState.current &&
      !openItems.has(accordionId)
    ) {
      toggleItem(accordionId);
      hasSetInitialState.current = true;
    }
  }, [defaultOpen, accordionId, toggleItem, openItems]);

  const isOpen = openItems.has(accordionId);

  // Calculate and monitor content height
  useEffect(() => {
    const updateHeight = () => {
      if (contentInnerRef.current) {
        const innerHeight = isOpen ? contentInnerRef.current.offsetHeight : 0;
        setContentHeight(innerHeight);
      }
    };

    // Update height immediately
    updateHeight();

    // Set up ResizeObserver if accordion is open
    if (isOpen && contentInnerRef.current) {
      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(contentInnerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [isOpen, children]);

  const handleToggle = () => {
    if (!disabled) {
      // Get the current state before toggling
      const willBeOpen = !isOpen;

      // Execute the custom toggle action before the built-in toggle
      onToggle?.(willBeOpen, accordionId);

      // Perform the built-in toggle functionality
      toggleItem(accordionId);
    }
  };

  const handleTitleClick = (e: MouseEvent) => {
    if (titleEditable && !disabled) {
      e.stopPropagation();
      setIsEditingTitle(true);
      handleToggle();
    }
  };

  const handleTitleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    setTitleValue(value);
    onTitleChange?.(value);
  };

  const handleTitleInputBlur = (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!titleError) {
      setIsEditingTitle(false);
    }
    onTitleBlur?.(e);
  };

  const handleTitleInputKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === "Escape") {
      setIsEditingTitle(false);
    } else if (e.key === "Enter" && !titleError) {
      setIsEditingTitle(false);
    }
    onTitleKeyDown?.(e);
  };

  return (
    <div
      className={classNames("accordion", className, {
        "accordion-disabled": disabled,
      })}
      id={accordionId}
    >
      <div
        className={classNames("accordion-header", headerClassName, {
          "accordion-header-open": isOpen,
        })}
      >
        {leftElement && (
          <div className="accordion-left-element">{leftElement}</div>
        )}
        {titleNumber && (
          <div className="accordion-title-number flex items-center pr-3">
            {titleNumber}
          </div>
        )}

        {titleEditable && isEditingTitle ? (
          <div className="flex flex-col flex-1">
            <EmeraldInput
              value={titleValue}
              onChange={handleTitleInputChange}
              onBlur={handleTitleInputBlur}
              onKeyDown={handleTitleInputKeyDown}
              className={classNames("accordion-title-input", titleInputClass)}
              inputSize="small"
              outlined={false}
              autoFocus
            />
            {titleError && (
              <span className="text-red-500 text-xs mt-1 px-1">
                {titleError}
              </span>
            )}
          </div>
        ) : (
          <button
            type="button"
            className={classNames("accordion-title text-base! font-normal!", {
              "cursor-pointer": titleEditable && !disabled,
            })}
            onClick={titleEditable ? handleTitleClick : handleToggle}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-controls={`${accordionId}-content`}
            tabIndex={-1}
          >
            {title}
          </button>
        )}

        {rightElement && (
          <div className="accordion-right-element">{rightElement}</div>
        )}

        <button
          onClick={handleToggle}
          tabIndex={-1}
          type="button"
          aria-controls={`${accordionId}-content`}
          className="accordion-indicator"
        >
          <EmeraldIcon
            icon={isOpen ? "expand_less" : "expand_more"}
            className={classNames("accordion-arrow", {
              "accordion-arrow-open": isOpen,
            })}
          />
        </button>
      </div>

      <div
        id={`${accordionId}-content`}
        className={classNames("accordion-content", contentClassName, {
          "accordion-content-open": isOpen,
        })}
        style={{
          height: `${contentHeight}px`,
        }}
        aria-hidden={!isOpen}
      >
        <div ref={contentInnerRef} className="accordion-content-inner">
          {children}
        </div>
      </div>
    </div>
  );
};

// Add displayName for better debugging
Accordion.displayName = "Accordion";

// Export both components as default export for convenience
const AccordionComponents = {
  AccordionGroup,
  Accordion,
};

export default AccordionComponents;
